<script setup lang="ts">
import BaseLayout from "@/layouts/BaseLayout.vue";
import {CapacityFramework} from "@/types";
import CapacityFrameworkLayout from "@/layouts/CapacityFrameworkLayout.vue";
import {Link} from "@inertiajs/vue3";
import {route} from "ziggy-js";
import {useTeam} from "@/composables/team";

defineOptions({layout: [BaseLayout, CapacityFrameworkLayout]})

const {isGuest} = useTeam()

interface Props {
    title: string
    capacityFramework: CapacityFramework
}

const props = defineProps<Props>();
</script>

<template>
    <v-row justify="center">
        <v-col
            cols="12"
            class="border-t-sm"
        >
            <v-alert
                v-if="isGuest"
                variant="tonal"
                type="warning"
                color="error"
                class="mb-3"
            >
                <p class="text-body-2">
                    To start an assessment you need to be part of a resource. You can either ask an existing resource admin to add you to their resource or <Link :href="route('teams.create')">
                        create your own resource
                    </Link>.
                </p>
            </v-alert>

            <v-alert
                v-if="props.capacityFramework.version_id !== props.capacityFramework.latest_version"
                variant="tonal"
                type="warning"
                color="error"
                class="mb-3"
            >
                <p class="text-body-2">
                    You are viewing an older version of this framework. The latest version is <Link :href="route('capacity-frameworks.show', {capacityFramework: props.capacityFramework.id})">
                        v{{ props.capacityFramework.latest_version }}
                    </Link>.
                </p>
            </v-alert>

            <v-alert
                v-if="props.capacityFramework.status === 'draft'"
                variant="tonal"
                type="info"
                color="primary"
                closable
            >
                <span class="text-body-2 text-black">
                    This current version of "{{ props.capacityFramework.name }}" is currently being drafted and is not yet published. Only once it is published, it will be available for assessments.
                </span>
            </v-alert>
        </v-col>
        <v-col cols="12">
            <div class="text-h5 font-weight-bold">
                Metrics
            </div>
            <v-divider class="mt-1 mb-3" />
            <p class="text-body-2 align-content-center my-3">
                <v-icon
                    size="small"
                    class="mr-2"
                    color="grey-darken-2"
                >
                    mdi-text
                </v-icon>
                <span class="font-weight-bold">
                    {{ props.capacityFramework.categories_count }}
                </span>
                Categories
            </p>
            <p class="text-body-2 align-content-center my-3">
                <v-icon
                    size="small"
                    class="mr-2"
                    color="grey-darken-2"
                >
                    mdi-map-marker-question-outline
                </v-icon>
                <span class="font-weight-bold">
                    {{ props.capacityFramework.capacity_indicators_count }}
                </span>
                Capacity indicators
            </p>
        </v-col>
        <v-col cols="12">
            <div class="text-h5 font-weight-bold">
                Description
            </div>
            <v-divider class="mt-1 mb-3" />
            <p
                class="text-pre px-1"
                v-html="props.capacityFramework.description"
            />
        </v-col>
    </v-row>
</template>

<style scoped>
</style>
